import type { ReactNode } from "react";
import {
  absenceMarkerClassName,
  formatAbsenceMarkerLabel,
  getAbsenceTypeLabel,
  type AbsenceDayMarker,
} from "@/lib/employeeAbsences";
import { calendarDayTitle, formatCalendarDayShort } from "@/lib/employeeCalendar";
import {
  formatCompactHours,
  formatDayHeading,
  formatOvertimeBalanceHhmm,
  todayIso,
  type YearGridCell,
} from "@/pages/admin/zeiterfassung/yearOverviewUtils";

interface YearOverviewCellProps {
  cell: YearGridCell;
  isToday: boolean;
  isSelected?: boolean;
  onSelect: (cell: YearGridCell) => void;
}

function hoursTone(hours: number): string {
  if (hours >= 8) return "year-day--hours-high";
  if (hours >= 4) return "year-day--hours-mid";
  return "year-day--hours-low";
}

function buildTitle(cell: YearGridCell): string {
  const parts: string[] = [formatDayHeading(cell.iso)];
  if (cell.hours > 0) parts.push(`${formatCompactHours(cell.hours)} h erfasst (App)`);
  if (cell.heroHours > 0) parts.push(`${formatCompactHours(cell.heroHours)} h in Hero`);
  if (cell.overtimeBalanceHours != null) {
    const projected = cell.iso > todayIso();
    parts.push(
      `${projected ? "Prognose" : "Aktuell"} ${formatOvertimeBalanceHhmm(cell.overtimeBalanceHours)}`,
    );
  }
  if (cell.payoutHours > 0) {
    parts.push(`${formatCompactHours(cell.payoutHours)} h ausgezahlt`);
  }
  if (cell.calendarDay) parts.push(calendarDayTitle(cell.calendarDay));
  for (const marker of cell.markers) {
    parts.push(`${getAbsenceTypeLabel(marker.absence_type)} (App)`);
  }
  for (const marker of cell.heroMarkers) {
    parts.push(`${getAbsenceTypeLabel(marker.absence_type)} (Hero)`);
  }
  return parts.join(" · ");
}

function AbsenceChip({
  marker,
  fromHero,
}: {
  marker: AbsenceDayMarker;
  fromHero?: boolean;
}) {
  return (
    <span
      className={[
        "year-day__chip",
        fromHero ? "year-day__chip--hero" : "",
        absenceMarkerClassName(marker),
      ]
        .filter(Boolean)
        .join(" ")}
      title={
        fromHero
          ? `Hero: ${getAbsenceTypeLabel(marker.absence_type)}`
          : getAbsenceTypeLabel(marker.absence_type)
      }
    >
      {formatAbsenceMarkerLabel(marker)}
    </span>
  );
}

export function YearOverviewCell({
  cell,
  isToday,
  isSelected = false,
  onSelect,
}: YearOverviewCellProps) {
  const hasAppHours = cell.hours > 0;
  const hasHeroHours = cell.heroHours > 0;
  const hasAppMarkers = cell.markers.length > 0;
  const hasHeroMarkers = cell.heroMarkers.length > 0;
  const hasPayout = cell.payoutHours > 0;
  const isHoliday = cell.calendarDay?.kind === "holiday";
  const isBridge = cell.calendarDay?.kind === "bridge";
  const isBetriebsferien = cell.calendarDay?.kind === "betriebsferien";
  const hasAbsenceMarkers = hasAppMarkers || hasHeroMarkers;
  const showHeroHours = hasHeroHours && !hasAbsenceMarkers;
  const hasBalance = cell.overtimeBalanceHours != null;
  const balanceNegative =
    hasBalance && cell.overtimeBalanceHours != null && cell.overtimeBalanceHours < -0.0001;
  const balanceProjected = hasBalance && cell.iso > todayIso();

  const pendingJustification = Boolean(cell.pendingJustification);

  let tone = "year-day--empty";
  if (isHoliday) tone = "year-day--holiday";
  else if (isBetriebsferien) tone = "year-day--betriebsferien";
  else if (isBridge) tone = "year-day--bridge";
  else if (pendingJustification) tone = "year-day--pending-justification";
  else if (hasAppHours) tone = hoursTone(cell.hours);
  else if (showHeroHours) tone = "year-day--hero-hours";
  else if (hasAbsenceMarkers) tone = hasHeroMarkers ? "year-day--hero-absence" : "year-day--absence";
  else if (hasPayout) tone = "year-day--payout";

  let value: ReactNode = null;

  if (hasAppHours || showHeroHours || hasBalance) {
    value = (
      <div className="year-day__hours-row">
        {hasAppHours && (
          <span className="year-day__hours">{formatCompactHours(cell.hours)}</span>
        )}
        {showHeroHours && (
          <span className="year-day__hours year-day__hours--hero year-day__hero-fade-in" title="Hero">
            {formatCompactHours(cell.heroHours)}
          </span>
        )}
        {hasBalance && cell.overtimeBalanceHours != null && (
          <span
            className={[
              "year-day__balance",
              balanceNegative ? "year-day__balance--negative" : "year-day__balance--positive",
              balanceProjected ? "year-day__balance--projected" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            title={
              balanceProjected
                ? "Prognostizierter Überstundenstand (ohne künftige Ist-Zeiten)"
                : "Aktueller Überstundenstand"
            }
          >
            {formatOvertimeBalanceHhmm(cell.overtimeBalanceHours)}
          </span>
        )}
      </div>
    );
  }

  const chips: ReactNode[] = [];
  if (pendingJustification) {
    chips.push(
      <span
        key="pending-justification"
        className="year-day__chip year-day__chip--pending-justification"
        title="Begründung fehlt – Tag zählt noch nicht in der Zeiterfassung"
      >
        Begr.
      </span>,
    );
  }
  if (hasPayout) {
    chips.push(
      <span
        key="payout"
        className="year-day__chip year-day__chip--payout"
        title={`Ausgezahlt: ${formatCompactHours(cell.payoutHours)} h`}
      >
        Ausz.
      </span>,
    );
  }
  for (const marker of cell.markers) {
    chips.push(<AbsenceChip key={`app-${marker.absence_type}-${marker.date}`} marker={marker} />);
  }
  for (const marker of cell.heroMarkers) {
    chips.push(
      <AbsenceChip
        key={`hero-${marker.absence_type}-${marker.date}`}
        marker={marker}
        fromHero
      />,
    );
  }

  if (chips.length > 0) {
    value = (
      <>
        {value}
        <div className="year-day__chips">{chips}</div>
      </>
    );
  } else if (!value) {
    if ((isBridge || isBetriebsferien) && cell.calendarDay) {
      value = (
        <span
          className={
            isBetriebsferien
              ? "year-day__chip year-day__chip--betriebsferien"
              : "year-day__chip year-day__chip--bridge"
          }
        >
          {formatCalendarDayShort(cell.calendarDay)}
        </span>
      );
    }
  }

  const className = [
    "year-day",
    tone,
    cell.isWeekend ? "year-day--weekend" : "",
    isToday ? "year-day--today" : "",
    isSelected ? "year-day--selected" : "",
    chips.length > 0 || hasAppHours || showHeroHours || hasBalance
      ? "year-day--has-content"
      : "",
    hasBalance ? "year-day--has-balance" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      title={buildTitle(cell)}
      aria-label={buildTitle(cell)}
      aria-pressed={isSelected || undefined}
      onClick={() => onSelect(cell)}
    >
      {isHoliday && (
        <span className="year-day__holiday-mark" aria-hidden>
          FT
        </span>
      )}
      <span className="year-day__num">{cell.day}</span>
      {value}
    </button>
  );
}
